"use client";

import { Button } from "@papier/ui/components/ui/button";
import { Label } from "@papier/ui/components/ui/label";
import { Plus, Sparkles } from "lucide-react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { getDefaultExampleForType } from "@/utils/schema-utils";
import { PrimitiveExampleEditor } from "./primitive-example-editor";
import { ArrayExampleEditor } from "./array-example-editor";
import { ObjectExampleEditor } from "./object-example-editor";

interface ExampleEditorProps {
  field: SchemaField;
  onUpdateField: (id: string, updates: Partial<SchemaField>) => void;
}

export function ExampleEditor({ field, onUpdateField }: ExampleEditorProps) {
  if (!field.examples) return null;

  const addExample = () => {
    const newExample = getDefaultExampleForType(field.type, field);
    onUpdateField(field.id, { examples: [...field.examples!, newExample] });
  };

  const removeExample = (index: number) => {
    const newExamples = field.examples!.filter((_, i) => i !== index);
    onUpdateField(field.id, { examples: newExamples });
  };

  const updateExample = (index: number, value: any) => {
    const newExamples = [...field.examples!];
    newExamples[index] = value;
    onUpdateField(field.id, { examples: newExamples });
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <Sparkles className="h-4 w-4" />
        Examples
      </Label>
      {field.examples.map((example, index) => (
        <div key={index} className="space-y-2">
          {field.type === "array" ? (
            <ArrayExampleEditor
              example={example}
              arrayField={field}
              onUpdate={(newExample) => updateExample(index, newExample)}
              onRemove={() => removeExample(index)}
            />
          ) : field.type === "object" ? (
            <ObjectExampleEditor
              example={example}
              objectField={field}
              onUpdate={(newExample) => updateExample(index, newExample)}
              onRemove={() => removeExample(index)}
            />
          ) : (
            <PrimitiveExampleEditor
              example={example}
              field={field}
              onUpdate={(newExample) => updateExample(index, newExample)}
              onRemove={() => removeExample(index)}
            />
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addExample} className="w-full bg-transparent">
        <Plus className="mr-2 h-4 w-4" />
        Add Example
      </Button>
    </div>
  );
}
